import * as Sentry from '@sentry/react'
import { env } from './env'

export interface ApiErrorReport {
  method: string
  /** Route template, e.g. `/condominiums/:id/units`: never the concrete URL or any body. */
  pathTemplate: string
  status: number
  requestId: string
}

export interface Telemetry {
  init(): void
  captureError(error: unknown, context?: Record<string, unknown>): void
  captureApiError(report: ApiErrorReport): void
  breadcrumb(category: string, message: string, data?: Record<string, unknown>): void
  setAccessContext(ctx: { condominiumId: number | null; capabilities: string[] }): void
}

/* ---- Scrubbing (tokens, CPF, phone, email) ---- */

const REDACTED = '[redacted]'
const SENSITIVE_KEY =
  /authorization|cookie|token|secret|password|tax_?id|cpf|e-?mail|phone|poll_secret|code/i

const PATTERNS: [RegExp, string][] = [
  [/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [token]'],
  [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '[token]'],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  [/\+55[\s-]?\(?\d{2}\)?[\s-]?9?\d{4}[\s-]?\d{4}/g, '[phone]'],
  [/\(?\b\d{2}\)?\s?9?\d{4}-\d{4}\b/g, '[phone]'],
  [/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, '[cpf]'],
]

export function scrubString(value: string): string {
  return PATTERNS.reduce((acc, [re, to]) => acc.replace(re, to), value)
}

/** Recursively scrubs strings and blanks values under sensitive keys. */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (depth > 8 || value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1))
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [
      k,
      SENSITIVE_KEY.test(k) && v != null && typeof v !== 'object'
        ? REDACTED
        : scrubValue(v, depth + 1),
    ]),
  )
}

/** Sentry `beforeSend`: scrubs the event and drops request bodies and cookies. */
export function scrubEvent<T extends Sentry.ErrorEvent>(event: T): T {
  const scrubbed = scrubValue(event) as T
  if (scrubbed.request) {
    delete scrubbed.request.data
    delete scrubbed.request.cookies
  }
  return scrubbed
}

/* ---- Implementations ---- */

const noop: Telemetry = {
  init() {},
  captureError(error) {
    if (import.meta.env.DEV) console.error(error)
  },
  captureApiError() {},
  breadcrumb() {},
  setAccessContext() {},
}

const sentry: Telemetry = {
  init() {
    Sentry.init({
      dsn: env.sentryDsn,
      environment: env.appEnv,
      release: env.commitSha,
      sendDefaultPii: false,
      beforeSend: scrubEvent,
      beforeBreadcrumb: (b) => scrubValue(b) as Sentry.Breadcrumb,
    })
    window.addEventListener('unhandledrejection', (e) => this.captureError(e.reason))
  },
  captureError(error, context) {
    Sentry.captureException(error, { extra: scrubValue(context) as Record<string, unknown> })
  },
  captureApiError({ method, pathTemplate, status, requestId }) {
    Sentry.captureMessage(`API ${status} ${method} ${pathTemplate}`, {
      level: status >= 500 ? 'error' : 'warning',
      tags: { api_status: String(status), api_path: pathTemplate },
      extra: { method, pathTemplate, status, requestId },
      fingerprint: ['api-error', method, pathTemplate, String(status)],
    })
  },
  breadcrumb(category, message, data) {
    Sentry.addBreadcrumb({ category, message, data: scrubValue(data) as Record<string, unknown> })
  },
  setAccessContext({ condominiumId, capabilities }) {
    Sentry.setTag('condominium_id', condominiumId == null ? 'none' : String(condominiumId))
    Sentry.setTag('capabilities', capabilities.join(',') || 'none')
  },
}

/** Sentry only when VITE_SENTRY_DSN is set, so local stays silent. */
export const telemetry: Telemetry = env.sentryDsn ? sentry : noop
